import { useState } from 'react';
import { Link } from 'react-router';

import { Logo } from '@/components/brand/Logo';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { ApiError, api } from '@/lib/api';

/**
 * セルフサインアップ。
 *
 * ここではまだテナントを作らない。確認メールのリンクを踏んで初めて開通する
 * (確認できていないメールでアカウントを押さえられないようにするため)。
 */

interface Accepted {
  email: string;
  sent: boolean;
  /** メールを送れない環境でだけ返る (ローカル・E2E) */
  verifyUrl?: string;
}

export function SignupPage() {
  const [orgName, setOrgName] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState<Accepted | null>(null);

  const ready =
    orgName.trim() !== '' && name.trim() !== '' && email.trim() !== '' && password.length >= 12;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      setAccepted(
        await api.post<Accepted>('/api/signup', {
          orgName: orgName.trim(),
          name: name.trim(),
          email: email.trim(),
          password,
        }),
      );
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : '通信に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <header className="border-b border-line bg-surface-raised px-5 py-4 sm:px-8 sm:py-5 md:px-14">
        <Link to="/">
          <Logo height={30} className="text-brand-500" />
        </Link>
      </header>

      <main className="mx-auto flex w-full max-w-2xl flex-1 flex-col gap-6 px-5 py-10 sm:gap-8 sm:px-6 sm:py-16">
        {accepted ? (
          <section className="flex flex-col gap-4 border border-line bg-surface-raised px-5 py-6 sm:px-8 sm:py-8">
            <h1 className="text-xl font-bold">確認メールを送りました</h1>
            <p className="text-sm leading-relaxed text-ink-muted">
              <span className="font-mono">{accepted.email}</span> 宛のリンクを開くと、
              すぐに利用を開始できます。リンクは 24 時間で失効します。
            </p>
            {accepted.verifyUrl ? (
              <div className="flex flex-col gap-2 border border-line-strong bg-surface px-4 py-4">
                <span className="font-mono text-[11px] tracking-wider text-ink-faint">
                  DEV: メール送信が無効な環境のため、ここにリンクを出しています
                </span>
                <a
                  href={accepted.verifyUrl}
                  className="font-mono text-xs break-all text-brand-700 underline"
                >
                  {accepted.verifyUrl}
                </a>
              </div>
            ) : null}
          </section>
        ) : (
          <>
            <div className="flex flex-col gap-2">
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">14 日間無料で試す</h1>
              <p className="text-sm leading-relaxed text-ink-muted">
                クレジットカードは要りません。トライアル中は Full Management と MCP Connect を 5
                席ずつ使えます。
              </p>
            </div>

            <section className="flex flex-col gap-5 border border-line bg-surface-raised px-5 py-6 sm:px-8 sm:py-8">
              <Field label="組織名">
                <Input
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder="株式会社インオウ"
                />
              </Field>

              <Field label="あなたの名前">
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="管理 太郎"
                />
              </Field>

              <Field label="メールアドレス" hint="ここに確認リンクを送ります">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </Field>

              <Field label="パスワード" hint="12 文字以上">
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </Field>

              {error ? <p className="text-sm text-alert">{error}</p> : null}

              <Button
                variant="primary"
                className="w-full justify-center px-7 py-3 sm:w-auto sm:self-start"
                onClick={submit}
                disabled={busy || !ready}
              >
                確認メールを送る
              </Button>
            </section>

            <p className="text-xs text-ink-subtle">
              すでにアカウントをお持ちの方は
              <Link to="/login" className="text-brand-700 underline">
                ログイン
              </Link>
              してください。
            </p>
          </>
        )}
      </main>
    </div>
  );
}
